import React from 'react';
import { AlertTriangle, Info, UserCircle, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import api from '../../lib/api';
import logger from '../../lib/logger';
import useModules from '../../lib/useModules';
import { ActionLink } from './ActionLink';
import { PIELiveCounterStrip } from './PIELiveCounterStrip';

/**
 * PersonalImpactEntryTile — entry into the Personal Impact Engine
 * (/me) from the LIE landing page. Probes tenant readiness via the
 * (cheap, public-to-the-org) personas list endpoint:
 *
 *   • Seeded (>=1 persona)  → standard "Open Personal Impact" CTA.
 *   • Unseeded + super admin → amber "Not yet seeded" banner + a
 *                              one-click jump to Data Health so the
 *                              admin can seed before any team member
 *                              hits an empty page.
 *   • Unseeded + regular user → soft note ("Ask your super admin to
 *                              set this up") so they don't waste a
 *                              click.
 *
 * Lazy-loaded; failures are silent (the tile shrinks to nothing
 * rather than blocking the rest of the LIE landing).
 */
export function PersonalImpactEntryTile({ navigate }) {
  const [seeded, setSeeded] = React.useState(null); // null = unknown / loading
  const [stats, setStats] = React.useState(null);
  const { isSuperAdmin } = useModules();

  React.useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get('/personal/personas'),
      api.get('/personal/stats').catch(() => ({ data: null })),
    ])
      .then(([personasRes, statsRes]) => {
        if (cancelled) return;
        const list = personasRes?.data?.personas || [];
        setSeeded(list.length > 0);
        if (statsRes?.data) setStats(statsRes.data);
      })
      .catch((e) => {
        if (cancelled) return;
        logger.warn('PersonalImpactEntryTile readiness probe failed', e);
        setSeeded(true); // permissive — let the user click through
      });
    return () => { cancelled = true; };
  }, []);

  const isUnseeded = seeded === false;

  return (
    <Card
      className={cn(
        'mt-4 border-2 transition-colors',
        isUnseeded ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200 hover:border-violet-300',
      )}
      data-testid="lie-personal-impact-tile"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <UserCircle className="h-5 w-5 text-violet-600" />
          Individual Focus
          <span className="text-[10px] uppercase tracking-wider text-violet-600 bg-violet-100 px-2 py-0.5 rounded-full font-semibold ml-auto">
            Seertech Mirror
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">
          Zoom from org-level metrics down to a single contributor — surface their
          Capability and Confidence profile, simulate a high-leverage action, validate
          the realised Behavior change, and let the AI Coach hold the loop together.
        </p>

        {isUnseeded && isSuperAdmin && (
          <div
            className="flex items-start gap-3 p-3 rounded-lg border border-amber-300 bg-amber-100/60"
            data-testid="lie-pie-unseeded-admin"
          >
            <AlertTriangle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-amber-900">
                Seertech Mirror not yet seeded for this tenant
              </p>
              <p className="text-[11px] text-amber-800 mt-0.5">
                One-time setup. Open Data Health and click <strong>&quot;Seed Mirror Personas&quot;</strong>.
              </p>
            </div>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-7 px-3 flex-shrink-0"
              onClick={() => navigate('/admin/data-health')}
              data-testid="lie-pie-seed-cta"
            >
              <Sparkles className="h-3 w-3 mr-1" /> Set up
            </Button>
          </div>
        )}

        {isUnseeded && !isSuperAdmin && (
          <div
            className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50"
            data-testid="lie-pie-unseeded-user"
          >
            <Info className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-800">
              Seertech Mirror isn&apos;t set up yet. Ask your super admin to enable it from
              <strong> Administration → Data Health</strong>.
            </p>
          </div>
        )}

        {!isUnseeded && (
          <div className="space-y-2">
            <ActionLink label="My current effectiveness" desc="Today's Capability + Behavior snapshot, streaks, and what changed since last visit" onClick={() => navigate('/mirror/dashboard')} />
            <ActionLink label="What should I improve?" desc="AI-ranked actions by predicted Capability and Behavior lift, ranked for me specifically" onClick={() => navigate('/mirror/improve')} />
            <ActionLink label="Did it work?" desc="Validate predicted vs realised Behavior change on every commitment, with a Coach's read takeaway" onClick={() => navigate('/mirror/validate')} />
            <ActionLink label="Talk to the Coach" desc="Claude-powered grounded coaching, anchored on this week's verdicts" onClick={() => navigate('/mirror/coach')} />

            {stats && stats.personas_total > 0 && (
              <PIELiveCounterStrip stats={stats} onClick={() => navigate('/mirror/validate')} />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
